import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  convertArticleToUpdate,
  Article,
  validate,
  defaultCreateArticle,
} from "../../../Models/Article";
import { ArticlesService } from "../../../services/logistic/articles";
import { useNavigate, useParams } from "react-router-dom";
import {
  callErrorToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { useAppContext } from "../../../AppProvider";
import { ErrorResponse } from "../../../Models/Error";

const articlesService = new ArticlesService();

/**
 * Props for the HandlerArticleProvider component.
 */
interface HandlerArticleProviderProps {
  children: ReactNode;
  callback?: () => void;
  autosave: boolean;
}

/**
 * Context value that will be shared with consumers.
 */
interface HandlerArticleContextValue {
  data: Partial<Article>;
  error: ErrorResponse<Article> | null;
  loader: boolean;
  getArticle: (id: string) => void;
  action: string;
  edit: (article: Article, property: keyof Article, save?: boolean) => void;
}

// Create the context with an initial value of undefined
const HandlerArticleContext = createContext<
  HandlerArticleContextValue | undefined
>(undefined);

/**
 * Provider component for managing article-related data and operations.
 * Handles retrieval, creation, and modification of articles.
 */
const HandlerArticleProvider: React.FC<HandlerArticleProviderProps> = ({
  children,
  autosave = false,
  callback,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dictionary, accessTokenPayload } = useAppContext();
  const action = window.location.pathname.split("/")[2];

  const [data, setData] = useState<Partial<Article>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<Article> | null>(null);

  const prevError = useRef<ErrorResponse<Article>>(null);

  /**
   * Fetches article details by ID.
   * @param id - The article ID.
   */
  const getArticle = (id: string): void => {
    setLoader(true);
    articlesService
      .get(id)
      .then((res) => {
        setLoader(false);
        setData(res.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  /**
   * Creates a new article and navigates to the edit page.
   */
  const create = (): void => {
    const newArticle = { ...defaultCreateArticle };
    newArticle.ownerId = accessTokenPayload.owner;

    articlesService
      .create(newArticle)
      .then((res) => {
        navigate(`/articles/edit/${res.data.id}`, { replace: true });
      })
      .catch((err) => {
        callErrorToast(err);
      });

    return;
  };

  const edit = (
    article: Article,
    property: keyof Article | null,
    save = true
  ): void => {
    article = cleanObject(article);
    if (property) {
      removeError(property, error, prevError, setError);
    }
    if (save && autosave) {
      const validation = validate(data as Article);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        return;
      }

      const articleUpdated = convertArticleToUpdate(article);
      articlesService
        .edit(articleUpdated)
        .then(() => {
          setData(article);
          if (save && callback) {
            callback();
          }
        })
        .catch((err) => {
          setLoader(false);
          setError(err);
        });
      return;
    }
    setData(article);
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (action === "create") {
      create();
      return;
    } else if (action === "clone") {
      return;
    } else if (id) {
      getArticle(id);
    }
  }, []);

  return (
    <HandlerArticleContext.Provider
      value={{
        data,
        loader,
        error,
        getArticle,
        edit,
        action,
      }}
    >
      {children}
    </HandlerArticleContext.Provider>
  );
};

/**
 * Custom hook to access the HandlerArticleContext.
 * Ensures that the context is used only within a HandlerArticleProvider.
 */
const useHandlerContext = (): HandlerArticleContextValue => {
  const context = useContext(HandlerArticleContext);
  if (!context) {
    throw new Error(
      "useHandlerArticleContext must be used within a HandlerArticleProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { HandlerArticleProvider, useHandlerContext };
